/**
 * ArtifactCard
 * ------------
 * Renders a single portfolio artifact inside the "Your work" list of the
 * Portfolio Kit workspace. Each card surfaces the artifact's identity at a
 * glance — title, archetype, lifecycle status, a short description preview,
 * the external URL (if the learner linked a repo or live demo), and the last
 * time the artifact was updated. A three-dot menu exposes Edit and Delete.
 *
 * The component is presentational only: data fetching, mutation, and modal
 * orchestration live in the parent (`YourWorkTracker`). Edit/Delete are
 * surfaced as callback props so the parent owns the action lifecycle and we
 * stay easy to test in isolation.
 *
 * Props:
 *   - artifact: the PortfolioArtifact to render. `archetype` and `status` are
 *     typed as strings on the API model so legacy/future slugs round-trip;
 *     we fall back to the raw slug when no human label is mapped.
 *   - onEdit:   invoked with the artifact when the learner picks Edit.
 *   - onDelete: invoked with the artifact when the learner picks Delete.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PublicIcon from "@mui/icons-material/Public";
import { PortfolioArtifact, checkArtifactSite } from "../../api/client";
import ArtifactRubric from "./ArtifactRubric";

interface ArtifactCardProps {
  artifact: PortfolioArtifact;
  onEdit: (artifact: PortfolioArtifact) => void;
  onDelete: (artifact: PortfolioArtifact) => void;
  /** When provided, the card shows a "View prompt" action that opens the
   *  generation this artifact was saved from. Omitted when the artifact has
   *  no linked generation. */
  onViewPrompt?: (artifact: PortfolioArtifact) => void;
}

const DESCRIPTION_MAX_CHARS = 150;

const ARCHETYPE_LABEL_KEYS: Record<string, string> = {
  "rag-agent": "ArtifactCard.archetypeLabelRagAgent",
  "multi-agent-researcher": "ArtifactCard.archetypeLabelMultiAgentResearcher",
  "hitl-workflow-agent": "ArtifactCard.archetypeLabelHitlWorkflowAgent",
};

const STATUS_LABEL_KEYS: Record<string, string> = {
  in_progress: "ArtifactCard.statusLabelInProgress",
  complete: "ArtifactCard.statusLabelComplete",
  archived: "ArtifactCard.statusLabelArchived",
};

const STATUS_COLORS: Record<
  string,
  "default" | "primary" | "success" | "warning"
> = {
  in_progress: "primary",
  complete: "success",
  archived: "default",
};

/** Per-variant i18n keys for {@link relativeTimeLabel}. "updated" backs
 *  {@link formatRelativeTime}; "checked" backs {@link checkedRelativeTime} —
 *  kept as separate key sets (rather than a runtime prefix swap) since
 *  translated strings can't be assumed to share an "Updated "/"checked "
 *  prefix the way the English source text does. */
const RELATIVE_TIME_KEYS = {
  updated: {
    recently: "ArtifactCard.updatedRecently",
    justNow: "ArtifactCard.updatedJustNow",
    minutes: "ArtifactCard.updatedMinutesAgo",
    hours: "ArtifactCard.updatedHoursAgo",
    days: "ArtifactCard.updatedDaysAgo",
    weeks: "ArtifactCard.updatedWeeksAgo",
    months: "ArtifactCard.updatedMonthsAgo",
    years: "ArtifactCard.updatedYearsAgo",
  },
  checked: {
    recently: "ArtifactCard.checkedRecently",
    justNow: "ArtifactCard.checkedJustNow",
    minutes: "ArtifactCard.checkedMinutesAgo",
    hours: "ArtifactCard.checkedHoursAgo",
    days: "ArtifactCard.checkedDaysAgo",
    weeks: "ArtifactCard.checkedWeeksAgo",
    months: "ArtifactCard.checkedMonthsAgo",
    years: "ArtifactCard.checkedYearsAgo",
  },
} as const;

/** Compact relative-time formatter. Avoids pulling in date-fns/dayjs for a
 *  single timestamp render. Produces strings like "just now", "5 minutes ago",
 *  "3 days ago", "2 months ago" (English via the `updated`/`checked` key sets
 *  above; pluralized with i18next's `_one`/`_other` suffixes). */
function relativeTimeLabel(
  t: TFunction,
  iso: string,
  variant: keyof typeof RELATIVE_TIME_KEYS,
): string {
  const keys = RELATIVE_TIME_KEYS[variant];
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return t(keys.recently);

  const diffSeconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSeconds < 45) return t(keys.justNow);

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return t(keys.minutes, { count: diffMinutes });
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return t(keys.hours, { count: diffHours });
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return t(keys.days, { count: diffDays });
  }

  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 5) {
    return t(keys.weeks, { count: diffWeeks });
  }

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return t(keys.months, { count: diffMonths });
  }

  const diffYears = Math.floor(diffDays / 365);
  return t(keys.years, { count: diffYears });
}

function formatRelativeTime(t: TFunction, iso: string): string {
  return relativeTimeLabel(t, iso, "updated");
}

/** Site-check verdict → Chip color + label key. Drives the small status pill
 *  shown next to the human-readable `message`. */
const SITE_CHECK_VERDICTS: Record<
  string,
  { color: "success" | "warning" | "error"; labelKey: string }
> = {
  ok: { color: "success", labelKey: "ArtifactCard.siteCheckLabelLive" },
  error_status: { color: "warning", labelKey: "ArtifactCard.siteCheckLabelIssue" },
  not_a_web_app: { color: "warning", labelKey: "ArtifactCard.siteCheckLabelIssue" },
  unreachable: { color: "error", labelKey: "ArtifactCard.siteCheckLabelUnreachable" },
  blocked: { color: "error", labelKey: "ArtifactCard.siteCheckLabelBlocked" },
};

/** "checked 5 minutes ago" */
function checkedRelativeTime(t: TFunction, iso: string): string {
  return relativeTimeLabel(t, iso, "checked");
}

/** Returns the URL's hostname for display, or the raw string if parsing
 *  fails (e.g., the learner saved a malformed URL). */
function safeHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}…`;
}

export default function ArtifactCard({
  artifact,
  onEdit,
  onDelete,
  onViewPrompt,
}: ArtifactCardProps) {
  const theme = useTheme();
  const { t } = useTranslation("portfolio-kit");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);

  // Per-card site reachability check. The card owns this self-contained action
  // (local pending state); on success we invalidate the list so the persisted
  // site_check_result re-renders from fresh server data.
  const qc = useQueryClient();
  const checkMutation = useMutation({
    mutationFn: (id: string) => checkArtifactSite(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["portfolioArtifacts"] }),
  });

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleCloseMenu();
    onEdit(artifact);
  };

  const handleDelete = () => {
    handleCloseMenu();
    onDelete(artifact);
  };

  const archetypeLabelKey = ARCHETYPE_LABEL_KEYS[artifact.archetype];
  const archetypeLabel = archetypeLabelKey
    ? t(archetypeLabelKey)
    : artifact.archetype;
  const statusLabelKey = STATUS_LABEL_KEYS[artifact.status];
  const statusLabel = statusLabelKey ? t(statusLabelKey) : artifact.status;
  const statusColor = STATUS_COLORS[artifact.status] ?? "default";

  const description = artifact.description?.trim() ?? "";
  const showDescription = description.length > 0;

  const externalUrl = artifact.external_url?.trim() ?? "";
  const showExternalUrl = externalUrl.length > 0;

  const siteResult = artifact.site_check_result;
  const verdictMeta = siteResult ? SITE_CHECK_VERDICTS[siteResult.status] : null;
  const verdict = siteResult
    ? {
        color: verdictMeta?.color ?? ("warning" as const),
        label: t(verdictMeta?.labelKey ?? "ArtifactCard.siteCheckLabelIssue"),
      }
    : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: 2,
        transition: "background-color 120ms ease, border-color 120ms ease",
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          borderColor: alpha(theme.palette.primary.main, 0.4),
        },
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
        {artifact.title}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: showDescription ? 1.5 : 1 }}>
        <Chip
          label={archetypeLabel}
          size="small"
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
            fontWeight: 500,
          }}
        />
        <Chip label={statusLabel} size="small" color={statusColor} />
      </Stack>

      {showDescription && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: showExternalUrl ? 1.5 : 2 }}
        >
          {truncate(description, DESCRIPTION_MAX_CHARS)}
        </Typography>
      )}

      {showExternalUrl && (
        <Box sx={{ mb: 2 }}>
          <Typography
            component="a"
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: theme.palette.primary.main,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
            {safeHostname(externalUrl)}
          </Typography>
        </Box>
      )}

      {showExternalUrl && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              size="small"
              startIcon={<PublicIcon sx={{ fontSize: 16 }} />}
              onClick={() => checkMutation.mutate(artifact.id)}
              disabled={checkMutation.isPending}
              sx={{
                color: theme.palette.primary.main,
                textTransform: "none",
                fontWeight: 500,
                minWidth: 0,
                px: 1,
              }}
            >
              {checkMutation.isPending
                ? t("ArtifactCard.checkingButton")
                : siteResult
                  ? t("ArtifactCard.recheckButton")
                  : t("ArtifactCard.checkSiteButton")}
            </Button>
            {verdict && siteResult && (
              <Tooltip title={siteResult.message}>
                <Chip label={verdict.label} size="small" color={verdict.color} />
              </Tooltip>
            )}
          </Stack>

          {checkMutation.isError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 0.5 }}
            >
              {t("ArtifactCard.checkFailedMessage")}
            </Typography>
          )}

          {verdict && siteResult && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              {siteResult.message}
              {artifact.last_site_check_at && (
                <>
                  {" · "}
                  {checkedRelativeTime(t, artifact.last_site_check_at)}
                </>
              )}
            </Typography>
          )}
        </Box>
      )}

      <ArtifactRubric artifact={artifact} />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {formatRelativeTime(t, artifact.updated_at)}
          </Typography>
          {onViewPrompt && (
            <Button
              size="small"
              startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
              onClick={() => onViewPrompt(artifact)}
              sx={{
                color: theme.palette.primary.main,
                textTransform: "none",
                fontWeight: 500,
                minWidth: 0,
                px: 1,
              }}
            >
              {t("ArtifactCard.viewPromptButton")}
            </Button>
          )}
        </Stack>
        <IconButton
          aria-label={t("ArtifactCard.artifactOptionsAriaLabel")}
          size="small"
          onClick={handleOpenMenu}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("ArtifactCard.editMenuItem")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
          </ListItemIcon>
          <ListItemText>{t("ArtifactCard.deleteMenuItem")}</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
}
